import React from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
} from "@mui/material";
import {
  Phone,
  Email,
  LocationOn,
  Inventory2,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { Order, OrderItem } from "../../../../types";
import { resolveImageUrl } from "../../../../utils/imageFallback";

interface OrderDetailsProps {
  order: Order;
}

const OrderDetails: React.FC<OrderDetailsProps> = ({ order }) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "1fr 1fr",
          },
          gap: { xs: 2, md: 3 },
        }}
      >
        {/* Order Items */}
        <Box>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontSize: { xs: "1.1rem", sm: "1.25rem" },
            }}
          >
            {t('admin.orderItems')}
          </Typography>
          <List>
            {order.items?.map((item: OrderItem) => (
              <ListItem key={item.id} sx={{ px: 0 }}>
                <ListItemAvatar>
                  <Avatar
                    variant="rounded"
                    src={resolveImageUrl(item.product?.images?.[0], '/placeholder-product.png')}
                    sx={{
                      bgcolor: "rgba(0,212,255,0.05)",
                      borderRadius: "8px",
                    }}
                  >
                    <Inventory2 />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={item.product?.title}
                  secondary={`Quantity: ${
                    item.quantity
                  } × €${item.unit_price.toFixed(2)}`}
                />
                <Typography
                  variant="body1"
                  fontWeight={600}
                >
                  €{item.total_price.toFixed(2)}
                </Typography>
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Shipping Details */}
        <Box>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontSize: { xs: "1.1rem", sm: "1.25rem" },
            }}
          >
            {t('admin.shippingDetails')}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <LocationOn
                sx={{
                  fontSize: 20,
                  color: "text.secondary",
                }}
              />
              <Typography variant="body2">
                {order.shipping_address.address_line1}
                {order.shipping_address.address_line2 &&
                  `, ${order.shipping_address.address_line2}`}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                ml: 3.5,
              }}
            >
              <Typography variant="body2">
                {order.shipping_address.city},{" "}
                {order.shipping_address.postal_code}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                ml: 3.5,
              }}
            >
              <Typography variant="body2">
                {order.shipping_address.country}
              </Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Phone
                sx={{
                  fontSize: 20,
                  color: "text.secondary",
                }}
              />
              <Typography variant="body2">
                {order.shipping_address.phone}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Email
                sx={{
                  fontSize: 20,
                  color: "text.secondary",
                }}
              />
              <Typography variant="body2">
                {order.buyer_id}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default OrderDetails;